/**
 * Hook para cálculos de métricas e totais
 *
 * Consome o motor unificado de validação (`validateOrder`), respeitando a policy
 * do fluxo (`validationMode`). O contrato de retorno é preservado para não
 * quebrar os consumers existentes.
 */

import { useMemo } from 'react';
import { OrderObject, ProductRules } from '../types/objects';
import { getValidationPolicy, validateOrder, ValidationMode } from '../utils/validators';
import { debugLog } from '../../../lib/terminalLogger';

interface UseMetricsCalculationProps {
  selectedObjects: OrderObject[];
  metrosVendidos: number;
  isMetros: boolean;
  /**
   * Regras do produto para validar dimensões. Quando omitido, os objetos são
   * avaliados pelo seu `status` já calculado (comportamento legado preservado).
   */
  productRules?: ProductRules;
  /**
   * Fluxo de validação. Default `official-save` mantém o comportamento atual
   * (comparação metragem vendida vs calculada bloqueia salvamento).
   */
  validationMode?: ValidationMode;
}

export const useMetricsCalculation = ({
  selectedObjects,
  metrosVendidos,
  isMetros,
  productRules,
  validationMode = 'official-save'
}: UseMetricsCalculationProps) => {
  // 🔍 DEBUG: Log quando selectedObjects muda
  try {
    if (selectedObjects.length === 0) {
      debugLog('⚠️ [METRICS] selectedObjects está VAZIO!', {
        length: 0,
        metrosVendidos,
        isMetros
      });
    }
  } catch {}
  
  // Número de objetos únicos (sem multiplicação)
  const totalUniqueObjects = selectedObjects.length;

  // Total de objetos considerando a quantidade de cada um (com multiplicação)
  const totalObjectsWithQuantity = useMemo(() => {
    return selectedObjects.reduce((sum, obj) => sum + obj.quantity, 0);
  }, [selectedObjects]);

  // Total de área/metragem considerando quantidade
  const totalAreaWithQuantity = useMemo(() => {
    if (isMetros) {
      // Soma apenas a altura (convertendo de cm para m) e multiplicando pela quantidade
      const totalLengthCm = selectedObjects.reduce((sum, obj) => sum + (obj.height * obj.quantity), 0);
      // Converter para metros e arredondar para cima quando houver mais de 2 dígitos após a vírgula
      const totalLengthM = totalLengthCm / 100;
      // Se tiver mais de 2 casas decimais, arredonda para cima
      const result = Math.ceil(totalLengthM * 100) / 100;
      
      // 🔍 LOG CÁLCULO METROS
      try {
        debugLog('📏 [METRICS] Cálculo Metragem', {
          tipo: 'METROS',
          totalLengthCm,
          totalLengthM,
          resultadoFinal: result,
          objetosDetalhes: selectedObjects.map(obj => ({
            nome: obj.nome,
            height: obj.height,
            quantity: obj.quantity,
            contribuicao: obj.height * obj.quantity
          }))
        });
      } catch {}
      
      return result;
    } else {
      // Para unidades, apenas soma a quantidade de objetos
      const result = selectedObjects.reduce((sum, obj) => sum + obj.quantity, 0);
      
      // 🔍 LOG CÁLCULO UNIDADES
      try {
        debugLog('📦 [METRICS] Cálculo Unidades', {
          tipo: 'UNIDADES',
          totalObjetos: selectedObjects.length,
          totalQuantidade: result,
          objetosDetalhes: selectedObjects.map(obj => ({
            nome: obj.nome,
            quantity: obj.quantity
          }))
        });
      } catch {}
      
      return result;
    }
  }, [selectedObjects, isMetros]);

  // Validação unificada (dimensão + metragem) conforme a policy do fluxo
  const { hasErrors, hasExceededMeterage, hasSaveErrors, metrageExcess } = useMemo(() => {
    const result = validateOrder({
      selectedObjects,
      productRules,
      metrosVendidos,
      totalAreaWithQuantity,
      isMetros,
      mode: validationMode
    });
    
    // 🔍 LOG RESULTADO FINAL DE VALIDAÇÃO
    try {
      debugLog('✅ [METRICS] Resultado Validação Final', {
        tipo: isMetros ? 'METROS' : 'UNIDADES',
        validationMode,
        totalAreaWithQuantity,
        metrosVendidos,
        hasErrors: result.hasErrors,
        hasExceededMeterage: result.hasExceededMeterage,
        hasSaveErrors: result.hasSaveErrors,
        botaoSalvarHabilitado: !result.hasSaveErrors
      });
    } catch {}
    
    return result;
  }, [selectedObjects, totalAreaWithQuantity, metrosVendidos, isMetros, productRules, validationMode]);

  return {
    totalUniqueObjects,
    totalObjectsWithQuantity,
    totalAreaWithQuantity,
    hasErrors,
    hasExceededMeterage,
    hasSaveErrors,
    metrageExcess,
    validationMode,
    /** Indica se o fluxo compara metragem vendida vs calculada (policy.blockOnMeterage). */
    compareSoldMeter: getValidationPolicy(validationMode, {
      validaDimensao: productRules?.valida_dimensao,
      validaMetragem: productRules?.valida_metragem,
    }).blockOnMeterage
  };
};
